/**
 * Extract headings from HTML content
 *
 * Used to build Table of Contents.
 * This is a server-compatible utility (no DOM required).
 */

import type { TocHeading } from '@/shared/lib/types/content';
import { tocAnchorSlug } from '@letsgethome/lib/wasm';

/**
 * Extract H2 and H3 headings from HTML and generate IDs.
 * Returns the modified HTML with IDs added, plus the extracted headings.
 */
export function extractHeadings(html: string): {
  html: string;
  headings: TocHeading[];
} {
  const headings: TocHeading[] = [];
  const seenIds = new Set<string>();

  // Match h2 and h3 tags (with optional existing id)
  const headingRegex = /<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi;

  const processedHtml = html.replace(
    headingRegex,
    (match: string, tag: string, attrs: string, content: string) => {
      // Extract text content (strip HTML tags)
      const text = content.replace(/<[^>]+>/g, '').trim();
      if (!text) return match;

      // Check for existing id in attrs
      const existingIdMatch = attrs.match(/id=["']([^"']+)["']/);
      let id = existingIdMatch?.[1];

      // Generate id if not present — the heading → anchor rule (lowercase,
      // strip, hyphenate, 50-char cap) is the Rust SSOT
      // (`domain_core::presentation::toc_anchor_slug` via domain-wasm).
      if (!id) {
        id = tocAnchorSlug(text);
      }

      // Ensure unique id
      let uniqueId = id;
      let counter = 2;
      while (seenIds.has(uniqueId)) {
        uniqueId = `${id}-${counter}`;
        counter++;
      }
      seenIds.add(uniqueId);

      const level = parseInt(tag.charAt(1), 10);
      headings.push({ anchorId: uniqueId, text, level });

      // Add id to the heading tag if not present
      if (existingIdMatch) {
        return match; // Keep original if id exists
      }
      return `<${tag}${attrs} id="${uniqueId}">${content}</${tag}>`;
    }
  );

  return { html: processedHtml, headings };
}
